import React, { useState } from "react";
import { Typography, Chip, Box, useTheme, Button, CircularProgress, Divider } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ConfirmationModal from "./Modal/ConfirmationModal";
import { ErrorSnackbar } from "./Snackbar/ErrorSnackbar";
import ModalComponent from "./Modal/Modal";
import useReactivateAssignappQuestionnaire from "../hooks/useReactivateAssignappQuestionnaire";

const STATUS_CONFIG = {
  Terminado: { bg: "#68a575" },
  Iniciado: { bg: "#43a9dc" },
  Pendiente: { bg: "#858585" },
  "No evaluable": { bg: "#ADA686" },
  Retirado: { bg: "#e08a4a" },
};

const renderContextContent = (context) => {
  if (context == null || (typeof context === "object" && Object.keys(context).length === 0)) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        Sin contexto
      </Typography>
    );
  }

  if (typeof context === "string") {
    return <Typography variant="body2">{context.trim() || "Sin contexto"}</Typography>;
  }

  if (typeof context === "object") {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px" }}>
        {Object.entries(context).map(([key, value]) => (
          <React.Fragment key={key}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
              {key}:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    );
  }

  return <Typography variant="body2">{String(context)}</Typography>;
};

const QuestionnaireDetailCard = ({ questionnaireItem, refetchUserDetail }) => {
  const theme = useTheme();
  const { reactivate, loading: reactivating } = useReactivateAssignappQuestionnaire();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showContextModal, setShowContextModal] = useState(false);

  const canReactivate = questionnaireItem.status === "Terminado" || questionnaireItem.status === "Retirado";

  const statusBg = STATUS_CONFIG[questionnaireItem.status]?.bg ?? "#858585";

  const handleConfirmReactivate = async () => {
    setShowConfirm(false);
    const response = await reactivate(questionnaireItem.assignapp_questionnaire_id);
    if (response?.error) {
      setErrorMessage(response.error.message || "Error al reactivar el cuestionario");
      setShowError(true);
    } else {
      refetchUserDetail?.();
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: "background.paper",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: theme.shadows[2] },
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Contenido principal */}
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            ID:
          </Typography>
          <Typography variant="body2">{questionnaireItem.id}</Typography>

          <Typography variant="body2" color="text.secondary">
            ID externo:
          </Typography>
          <Typography variant="body2">{questionnaireItem.external_id}</Typography>

          <Typography variant="body2" color="text.secondary">
            ID en la asignación:
          </Typography>
          <Typography variant="body2">{questionnaireItem.assignapp_questionnaire_id}</Typography>

          <Typography variant="body2" color="text.secondary">
            ID externo en la asignación:
          </Typography>
          <Typography variant="body2">{questionnaireItem.aq_external_id || "-"}</Typography>

          <Typography variant="body2" color="text.secondary">
            Título:
          </Typography>
          <Typography variant="body2">{questionnaireItem.title}</Typography>

          <Typography variant="body2" color="text.secondary">
            Tipo:
          </Typography>
          <Typography variant="body2">{questionnaireItem.type}</Typography>

          <Typography variant="body2" color="text.secondary">
            Contexto:
          </Typography>
          <Box>
            <Button
              size="small"
              variant="text"
              startIcon={<InfoOutlinedIcon fontSize="small" />}
              onClick={() => setShowContextModal(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                p: 0,
                minWidth: 0,
                color: theme.palette.secondary.main,
                "&:hover": {
                  color: "#B3B320",
                  backgroundColor: "transparent",
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                },
              }}
            >
              Ver contexto
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              {canReactivate && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={reactivating}
                  onClick={() => setShowConfirm(true)}
                  startIcon={reactivating ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon fontSize="small" />}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  Reactivar
                </Button>
              )}
            </Box>
            <Chip size="small" label={questionnaireItem.status} sx={{ fontSize: "0.75rem", fontWeight: 600, color: "white", bgcolor: statusBg }} />
          </Box>
        </Box>
      </Box>

      {/* Modal de contexto */}
      <ModalComponent open={showContextModal} handleClose={() => setShowContextModal(false)} width="500px">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Contexto de la asignación del cuestionario
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          {questionnaireItem.title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>{renderContextContent(questionnaireItem.context)}</Box>
      </ModalComponent>

      <ConfirmationModal
        open={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleConfirmReactivate}
        title="¿Reactivar cuestionario?"
        description="El cuestionario volverá a estar disponible y podrá ser completado nuevamente. ¿Desea continuar?"
        confirmText="Sí, reactivar"
        cancelText="Cancelar"
        confirmColor="warning"
      />

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
    </>
  );
};

export default QuestionnaireDetailCard;
